import React from "react";
import {
  LineChart, Line, XAxis, YAxis,
  Tooltip, ResponsiveContainer, ReferenceLine, CartesianGrid
} from "recharts";

// ─── VERIFIED DATA ────────────────────────────────────────────────────────────
// Source: user-verified price table, 1925 = 1.0x baseline for all series
const data = [
  { year: "1925", rca: 1.0,  gg: 1.0,  philco: 1.0,  sp: 1.0  },
  { year: "1926", rca: 1.3,  gg: 1.0,  philco: 1.2,  sp: 1.2  },
  { year: "1927", rca: 2.0,  gg: 2.0,  philco: 1.5,  sp: 1.27  },
  { year: "1928", rca: 5.3,  gg: 8.0,  philco: 2.5,  sp: 1.66  },
  { year: "1929", rca: 33.3, gg: 30.0, philco: 3.5,  sp: 2.35  },
  { year: "1930", rca: 6.7,  gg: 6.0,  philco: 4.5,  sp: 2.02  },
  { year: "1931", rca: 2.7,  gg: 2.0,  philco: 5.5,  sp: 1.51  },
  { year: "1932", rca: 1.3,  gg: 1.0,  philco: 6.0,  sp: 0.78  },
  { year: "1933", rca: 1.0,  gg: 0.2,  philco: 6.5,  sp: 1.67 },
  { year: "1934", rca: 0.8,  gg: null, philco: 7.0,  sp: 1.0  },
  { year: "1935", rca: 0.7,  gg: null, philco: 7.5,  sp: 0.88  },
];

const COLORS = {
  rca:    "#7c5cbf",
  gg:     "#d4621a",
  philco: "#1a8a5e",
  sp:     "#94a3b8",
};

const SERIES = [
  { key: "rca",    label: "RCA",              archetype: "The Tollbooth",          dash: false },
  { key: "gg",     label: "Grigsby-Grunow",   archetype: "The Premium Incumbent",  dash: true  },
  { key: "philco", label: "Philco †",          archetype: "The Scrappy Commoditizer", dash: false },
  { key: "sp",     label: "S&P Composite",    archetype: "Market Benchmark",       dash: true  },
];

// ─── TOOLTIP ─────────────────────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const sorted = [...payload].filter(p => p.value != null).sort((a, b) => b.value - a.value);
  return (
    <div style={{
      background: "#ffffff",
      border: "1px solid #e2e8f0",
      borderRadius: 8,
      padding: "12px 16px",
      fontFamily: "'DM Mono', monospace",
      boxShadow: "0 4px 24px rgba(0,0,0,0.10)",
      minWidth: 180,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#1e293b", marginBottom: 8, letterSpacing: 1 }}>
        {label}
      </div>
      {sorted.map(p => {
        const s = SERIES.find(s => s.key === p.dataKey);
        return (
          <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 20, marginBottom: 3 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: COLORS[p.dataKey], flexShrink: 0 }} />
              <span style={{ color: "#64748b", fontSize: 10 }}>{s?.label}</span>
            </div>
            <span style={{ color: COLORS[p.dataKey], fontSize: 11, fontWeight: 700 }}>
              {p.value.toFixed(1)}×
            </span>
          </div>
        );
      })}
    </div>
  );
};

// ─── MAIN ─────────────────────────────────────────────────────────────────────
export default function RadioBoomChart() {
  return (
    <div style={{
      background: "#f8f7f4",
      padding: "40px 32px 36px",
      fontFamily: "'DM Mono', monospace",
      maxWidth: 760,
      margin: "0 auto",
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500&family=Playfair+Display:wght@700&display=swap');
        * { box-sizing: border-box; }
      `}</style>

      {/* Header */}
      <div style={{ marginBottom: 28, borderBottom: "2px solid #1e293b", paddingBottom: 18 }}>
        <div style={{ fontSize: 10, letterSpacing: 3, color: "#94a3b8", textTransform: "uppercase", marginBottom: 8 }}>
          Historical Case Study · USA · 1925–1935
        </div>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: 26, color: "#1e293b",
          margin: "0 0 8px 0", lineHeight: 1.2, fontWeight: 700,
        }}>
          The Radio Boom: Three Companies, Three Outcomes
        </h2>
        <p style={{ color: "#64748b", fontSize: 11, lineHeight: 1.7, margin: 0, maxWidth: 580 }}>
          All series indexed to 1925 = 1.0×. RCA and Grigsby-Grunow show stock price multiples.
          Philco (privately held) shows estimated market value proxy. S&P Composite as benchmark.
        </p>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 24, marginBottom: 20, flexWrap: "wrap" }}>
        {SERIES.map(s => (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <svg width="28" height="12">
              {s.dash
                ? <line x1="0" y1="6" x2="28" y2="6" stroke={COLORS[s.key]} strokeWidth="2" strokeDasharray="5 3" />
                : <line x1="0" y1="6" x2="28" y2="6" stroke={COLORS[s.key]} strokeWidth="2.5" />
              }
            </svg>
            <div>
              <div style={{ fontSize: 11, color: "#1e293b", fontWeight: 500 }}>{s.label}</div>
              <div style={{ fontSize: 9, color: "#94a3b8", letterSpacing: 0.5 }}>{s.archetype}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Chart */}
      <div style={{ background: "#ffffff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "20px 12px 12px 4px", marginBottom: 20 }}>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={data} margin={{ top: 8, right: 20, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 6" stroke="#f1f5f9" vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fill: "#94a3b8", fontSize: 10, fontFamily: "'DM Mono', monospace" }}
              axisLine={{ stroke: "#e2e8f0" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#94a3b8", fontSize: 9, fontFamily: "'DM Mono', monospace" }}
              axisLine={false} tickLine={false}
              tickFormatter={v => `${v}×`}
              domain={[0, 36]}
              width={38}
              ticks={[0, 1, 5, 10, 15, 20, 25, 30, 35]}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#e2e8f0", strokeWidth: 1 }} />

            {/* Baseline */}
            <ReferenceLine y={1} stroke="#cbd5e1" strokeDasharray="4 3" />

            {/* Event markers */}
            <ReferenceLine x="1929" stroke="#f59e0b" strokeWidth={1} strokeDasharray="4 3" />
            <ReferenceLine x="1932" stroke="#ef4444" strokeWidth={1} strokeDasharray="4 3" />

            <Line type="monotone" dataKey="sp"
              stroke={COLORS.sp} strokeWidth={1.5} strokeDasharray="5 3"
              dot={false} activeDot={{ r: 4, fill: COLORS.sp, stroke: "#fff", strokeWidth: 2 }}
            />
            <Line type="monotone" dataKey="philco"
              stroke={COLORS.philco} strokeWidth={2.5}
              dot={false} activeDot={{ r: 5, fill: COLORS.philco, stroke: "#fff", strokeWidth: 2 }}
            />
            <Line type="monotone" dataKey="gg"
              stroke={COLORS.gg} strokeWidth={2.5} strokeDasharray="6 3"
              dot={false} connectNulls={false}
              activeDot={{ r: 5, fill: COLORS.gg, stroke: "#fff", strokeWidth: 2 }}
            />
            <Line type="monotone" dataKey="rca"
              stroke={COLORS.rca} strokeWidth={2.5}
              dot={false} activeDot={{ r: 5, fill: COLORS.rca, stroke: "#fff", strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>

        {/* Inline annotations */}
        <div style={{ display: "flex", gap: 20, padding: "8px 16px 0", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 12, height: 1, background: "#f59e0b" }} />
            <span style={{ fontSize: 9, color: "#94a3b8" }}>1929 peak</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <div style={{ width: 12, height: 1, background: "#ef4444" }} />
            <span style={{ fontSize: 9, color: "#94a3b8" }}>1932 market trough</span>
          </div>
        </div>
      </div>

      {/* Key events — static, no interaction */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 20 }}>
        {[
          {
            year: "1929",
            color: "#f59e0b",
            title: "The Peak",
            body: "RCA hits 33×. Grigsby-Grunow 30×. The S&P sits at just 2.9×. Black Tuesday collapses margin debt in weeks.",
          },
          {
            year: "1930–32",
            color: "#7c5cbf",
            title: "The Unravelling",
            body: "DOJ antitrust dismantles RCA's patent monopoly. Philco becomes #1 radio seller. GG attempts refrigerator pivot and fails.",
          },
          {
            year: "1933–35",
            color: "#ef4444",
            title: "The Reckoning",
            body: "GG bankrupt, brand sold for scraps. RCA at 0.7× — below its 1925 starting price. Philco at 7.5× and still growing.",
          },
        ].map(e => (
          <div key={e.year} style={{
            borderTop: `3px solid ${e.color}`,
            background: "#ffffff",
            border: `1px solid #e2e8f0`,
            borderTopColor: e.color,
            borderRadius: 8,
            padding: "12px 14px",
          }}>
            <div style={{ fontSize: 9, color: e.color, letterSpacing: 1.5, textTransform: "uppercase", marginBottom: 4 }}>
              {e.year} — {e.title}
            </div>
            <div style={{ fontSize: 10, color: "#475569", lineHeight: 1.65 }}>{e.body}</div>
          </div>
        ))}
      </div>



      {/* Footnotes */}
      <div style={{ fontSize: 9, color: "#94a3b8", lineHeight: 1.7, borderTop: "1px solid #e2e8f0", paddingTop: 12 }}>
        † Philco was privately held throughout this period. Value proxy estimated from documented revenue and market share data (PhilcoRadio.com Historical Library, Encyclopedia of Greater Philadelphia) — not a traded stock price.
        RCA data: Finaeon historical records; Stanford University Press <em>Bubbles and Crashes</em> Ch. 1.
        Grigsby-Grunow data: Antique Radio Classified <em>The Saga of Grigsby-Grunow/Majestic</em>; Wikipedia "Majestic Radios".
        S&P Composite from publicly available historical index data. All figures approximate. Not financial advice.
      </div>

    </div>
  );
}